import * as React from "react";
import {
  OverlayToaster,
  Position,
  type OverlayToaster as OverlayToasterType,
} from "@blueprintjs/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiAdmin from "../../../api/admin";
import { ConfirmationDialog } from "../../Admin/common/ConfirmationDialog";
import { DEACTIVATED_FOLDERS_QUERY_KEY } from "./folderAdminQueryKeys";

interface FolderAdminDeactivateDialogProps {
  folder: { id: number; label: string } | null;
  onClose: () => void;
  onDeactivated?: () => void;
}

export function FolderAdminDeactivateDialog(
  props: FolderAdminDeactivateDialogProps
) {
  const { folder, onClose, onDeactivated } = props;
  const toaster = React.useRef<OverlayToasterType>(null);
  const queryClient = useQueryClient();

  const deactivateFolderMutation = useMutation({
    mutationFn: async ({ folderId }: { folderId: number }) => {
      return ApiAdmin.deactivateFolderForFolderAdmin(folderId);
    },
    onError: (err: any) => {
      toaster.current?.show({
        message: err?.message || "Failed to deactivate folder. Please try again.",
        intent: "danger",
        timeout: 5000,
      });
    },
    onSuccess: async () => {
      toaster.current?.show({
        message: "Folder deactivated successfully.",
        intent: "success",
        timeout: 3000,
      });
      await queryClient.invalidateQueries({
        queryKey: DEACTIVATED_FOLDERS_QUERY_KEY,
      });
      onClose();
      onDeactivated?.();
    },
  });

  return (
    <>
      <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toaster} />
      <ConfirmationDialog
        entityType="Folder"
        entityName={folder?.label ?? ""}
        titleAction="Deactivate"
        actionText="Deactivate"
        actioningText="Deactivating..."
        icon="disable"
        message="This will deactivate the folder. It will no longer appear in the portal for folder members. You can reactivate it later from the deactivated folders menu in the toolbar."
        isOpen={folder !== null}
        onClose={onClose}
        isPending={deactivateFolderMutation.isPending}
        onConfirm={() => {
          if (!folder) return;
          deactivateFolderMutation.mutate({ folderId: folder.id });
        }}
      />
    </>
  );
}

export default FolderAdminDeactivateDialog;

